/**
 * IndexedDB read/write via `idb`. Saves are MULTI-SLOT (D16): each world is keyed
 * by its `profile.slotId` in a versioned object store, so New Game creates a new
 * save instead of overwriting the live one. localStorage is too small for boxes +
 * a whole society, so the save lives here; export/import-to-file (see
 * exportImport.ts) lets the owner back it up.
 *
 * Reads are defensive: a missing or corrupt save resolves to `null`, never throws
 * to the caller (a tired player must never hit a crash on load).
 *
 * Back-compat: earlier builds stored a single save under the key `"current"`. The
 * first slot-aware access migrates that legacy entry into its own slot (keyed by
 * its `slotId`) and removes the `"current"` key — the world is never lost.
 */
import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type { Save } from '@/shared';
import { migrate } from './migrations.js';
import { isSaveLike } from './validate.js';

/** Database + store names. Bump DB_VERSION only for IndexedDB structural changes
 *  (object stores/indexes) — NOT for Save schema changes (those use migrate()). */
export const DB_NAME = 'parallel-routes';
export const DB_VERSION = 1;
export const STORE_NAME = 'saves';
/** Legacy single-slot key (pre-D16). Migrated into a real slot on first access. */
export const SAVE_KEY = 'current';

interface StoreSchema extends DBSchema {
  [STORE_NAME]: {
    key: string;
    value: unknown;
  };
}

let dbPromise: Promise<IDBPDatabase<StoreSchema>> | null = null;
/** Guards the one-time legacy-`"current"` migration so it runs at most once. */
let legacyMigrated = false;

function getDb(): Promise<IDBPDatabase<StoreSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<StoreSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
}

/** Drop the cached connection (tests swap the backing IndexedDB between cases). */
export function resetDb(): void {
  if (dbPromise) {
    dbPromise.then((db) => db.close()).catch(() => {});
  }
  dbPromise = null;
  legacyMigrated = false;
}

/** Migrate + validate a raw stored value into a Save, or null if unreadable. */
function readSave(raw: unknown): Save | null {
  if (raw === undefined || raw === null) return null;
  let migrated: Save;
  try {
    migrated = migrate(raw);
  } catch {
    return null;
  }
  return isSaveLike(migrated) ? migrated : null;
}

/**
 * One-time, best-effort migration of a legacy single-slot save (stored under the
 * `"current"` key) into its own slot keyed by `slotId`. Idempotent and guarded so
 * it runs at most once per process; never throws. The world is never lost: only
 * after the slot write succeeds is the legacy key removed.
 */
async function migrateLegacyCurrent(db: IDBPDatabase<StoreSchema>): Promise<void> {
  if (legacyMigrated) return;
  legacyMigrated = true;
  try {
    const raw = await db.get(STORE_NAME, SAVE_KEY);
    const save = readSave(raw);
    if (!save) return;
    // If a slot entry already exists for this save (e.g. a prior partial migration),
    // don't clobber it — just clear the legacy key.
    const existing = await db.get(STORE_NAME, save.profile.slotId);
    if (existing === undefined) {
      await db.put(STORE_NAME, save, save.profile.slotId);
    }
    await db.delete(STORE_NAME, SAVE_KEY);
  } catch {
    // Best-effort: a hiccup here must never crash a load. The legacy key stays and
    // we'll try again next process (legacyMigrated resets via resetDb in tests).
    legacyMigrated = false;
  }
}

/** Persist a save into its own slot (keyed by `save.profile.slotId`). */
export async function saveSlotToDb(slotId: string, save: Save): Promise<void> {
  const db = await getDb();
  await db.put(STORE_NAME, save, slotId);
}

/** Load a single slot's save (migrated + validated), or null if absent/corrupt. */
export async function loadSlotFromDb(slotId: string): Promise<Save | null> {
  let raw: unknown;
  try {
    const db = await getDb();
    await migrateLegacyCurrent(db);
    raw = await db.get(STORE_NAME, slotId);
  } catch {
    return null;
  }
  return readSave(raw);
}

/** Delete a single slot. Never throws. */
export async function deleteSlotFromDb(slotId: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete(STORE_NAME, slotId);
  } catch {
    // Best-effort: a delete hiccup must never crash a scene.
  }
}

/**
 * List every save slot held locally, each as `{ slotId, save }`. Skips any
 * unreadable/corrupt entry rather than throwing, and folds in the legacy
 * `"current"` entry (migrating it first). Never throws.
 */
export async function listSavesInDb(): Promise<{ slotId: string; save: Save }[]> {
  try {
    const db = await getDb();
    await migrateLegacyCurrent(db);
    const keys = (await db.getAllKeys(STORE_NAME)) as string[];
    const out: { slotId: string; save: Save }[] = [];
    for (const key of keys) {
      if (key === SAVE_KEY) continue; // legacy key (should be gone post-migration)
      const save = readSave(await db.get(STORE_NAME, key));
      if (save) out.push({ slotId: save.profile.slotId, save });
    }
    return out;
  } catch {
    return [];
  }
}

// ── back-compat single-slot surface (routed to the most-recent slot) ───────────

/**
 * Persist the save to its own slot (back-compat alias of {@link saveSlotToDb} keyed
 * by the save's own `slotId`). Retained so callers that don't yet know about slots
 * keep working.
 */
export async function writeSaveToDb(save: Save): Promise<void> {
  await saveSlotToDb(save.profile.slotId, save);
}

/** Write an arbitrary raw value to the legacy `"current"` key (tests simulate a
 *  legacy/corrupt record there). */
export async function writeRaw(raw: unknown): Promise<void> {
  const db = await getDb();
  await db.put(STORE_NAME, raw, SAVE_KEY);
}

/**
 * Load the MOST-RECENT save (back-compat single-slot read): picks the slot with the
 * greatest `lastPlayedAt`, migrating a legacy `"current"` entry first. Returns
 * `null` when no save exists or all stored values are unreadable. Never throws.
 */
export async function loadSaveFromDb(): Promise<Save | null> {
  const slots = await listSavesInDb();
  if (slots.length === 0) return null;
  // Deterministic "most recent": by lastPlayedAt, then createdAt, then the unique
  // slotId — so two slots written in the same millisecond never resolve at random.
  let best = slots[0]!.save;
  for (const { save } of slots) {
    const a = save.profile;
    const b = best.profile;
    const newer =
      save.lastPlayedAt !== best.lastPlayedAt
        ? save.lastPlayedAt > best.lastPlayedAt
        : a.createdAt !== b.createdAt
          ? a.createdAt > b.createdAt
          : a.slotId > b.slotId;
    if (newer) best = save;
  }
  return best;
}
